import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LogIn, RefreshCw, ChevronLeft, TrendingUp, TrendingDown,
  DollarSign, Users, Repeat, Coins, PlayCircle, XCircle, CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import PushBroadcastPanel from "@/pages/AdminPushBroadcast";

/**
 * The Keeper's Vault — monetization dashboard.
 * Consumes the existing backend endpoints:
 *   GET /api/admin/mrr          — MRR + subs + churn
 *   GET /api/admin/cohorts      — utm.source × revenue + payer conversion
 *   GET /api/admin/ad-fillrate  — 7-day rewarded-ad completion + fill rate
 */
export default function AdminMonetizationPage() {
  const [auth, setAuth]         = useState(null); // null=checking | {is_admin, error?}
  const [mrr, setMrr]           = useState(null);
  const [cohorts, setCohorts]   = useState(null);
  const [fillrate, setFillrate] = useState(null);
  const [dynPrice, setDynPrice] = useState(null);
  const [fillAlerts, setFillAlerts] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]       = useState(null);

  const load = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [m, c, f, p, a] = await Promise.all([
        api.get("/admin/mrr").catch((e) => ({ __err: e })),
        api.get("/admin/cohorts").catch((e) => ({ __err: e })),
        api.get("/admin/ad-fillrate").catch((e) => ({ __err: e })),
        api.get("/ads/ad-free-pass/price").catch((e) => ({ __err: e })),
        api.get("/admin/ad-fillrate/alerts").catch((e) => ({ __err: e })),
      ]);
      if (!m?.__err) setMrr(m);
      if (!c?.__err) setCohorts(c);
      if (!f?.__err) setFillrate(f);
      if (!p?.__err) setDynPrice(p);
      if (!a?.__err) setFillAlerts(a);
      const firstErr = [m, c, f].find((x) => x?.__err)?.__err;
      if (firstErr && !m?.__err && !c?.__err && !f?.__err) setError(null);
      else if (firstErr && m?.__err && c?.__err && f?.__err) {
        const status = firstErr?.response?.status;
        if (status === 401) setAuth({ is_admin: false, error: "not_signed_in" });
        else if (status === 403) setAuth({ is_admin: false, error: "not_admin" });
        else setError("Could not load the ledger.");
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await api.get("/admin/me");
        setAuth({ is_admin: true });
        load();
      } catch (e) {
        if (e?.response?.status === 401) setAuth({ is_admin: false, error: "not_signed_in" });
        else setAuth({ is_admin: false, error: "not_admin" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goSignIn = () => {
    const redirectUrl = window.location.origin + "/admin/monetization";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (auth === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-heading text-2xl ash-text">Weighing the ember...</div>
      </div>
    );
  }
  if (!auth.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-mon-gate">
        <div className="panel p-10 max-w-lg text-center">
          <div className="text-[10px] tracking-[0.4em] blood-text">RESTRICTED</div>
          <h1 className="font-heading text-4xl mt-3 text-[#f0e6d2] italic">The Keeper&apos;s Vault</h1>
          <p className="ash-text mt-3 text-sm italic">
            {auth.error === "not_signed_in"
              ? "Sign in with an authorized administrator account."
              : "Your account does not have keeper's access."}
          </p>
          <div className="mt-6 flex justify-center gap-3">
            {auth.error === "not_signed_in" && (
              <button onClick={goSignIn} className="btn-primary flex items-center gap-2" data-testid="admin-mon-signin-btn">
                <LogIn size={14} /> SIGN IN
              </button>
            )}
            <Link to="/" className="btn-glow" data-testid="admin-mon-back-btn">RETURN TO REALM</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 md:px-8 lg:px-12 py-6" data-testid="admin-monetization-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="ash-text hover:text-white flex items-center gap-1 text-xs tracking-widest">
            <ChevronLeft size={14} /> DASHBOARD
          </Link>
          <div className="h-4 w-px bg-white/20" />
          <div>
            <div className="text-[10px] tracking-[0.4em] gold-text">THE KEEPER&apos;S VAULT</div>
            <div className="font-heading text-2xl text-[#f0e6d2] leading-none mt-1">Revenue, Cohorts &amp; Ads</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/admin"
            className="panel px-3 py-2 flex items-center gap-2 hover:border-[#d4af37] transition-colors"
            data-testid="admin-mon-attribution-link"
          >
            <span className="text-[10px] tracking-[0.3em] ash-text">← ATTRIBUTION</span>
          </Link>
          <button
            onClick={load}
            disabled={refreshing}
            className="panel px-3 py-2 flex items-center gap-2 hover:border-[#d4af37] transition-colors"
            data-testid="admin-mon-refresh-btn"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            <span className="text-[10px] tracking-[0.3em] ash-text">{refreshing ? "READING RUNES" : "REFRESH"}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="panel p-4 mb-6 border-red-500/40" data-testid="admin-mon-error">
          <div className="text-[10px] tracking-widest blood-text">{error}</div>
        </div>
      )}

      {/* --- MRR block --- */}
      <MrrBlock mrr={mrr} />

      {/* --- Cohorts by acquisition source --- */}
      <CohortsBlock cohorts={cohorts} />

      {/* --- Ad fillrate --- */}
      <FillrateBlock fillrate={fillrate} />

      {/* --- Dynamic Ad-Free Pass Pricing (iter67.3) --- */}
      <DynamicPriceBlock dynPrice={dynPrice} />

      {/* --- Fill-rate Alert Banner (iter67.3) --- */}
      <FillAlertBanner fillAlerts={fillAlerts} />

      {/* --- Push broadcast composer --- */}
      <PushBroadcastPanel />
    </div>
  );
}

// ============================================================
// MRR
// ============================================================
function MrrBlock({ mrr }) {
  if (!mrr) {
    return <div className="panel p-10 text-center ash-text italic mb-8">Counting the ember...</div>;
  }
  const growthPct = ((mrr.growth_rate_monthly || 0) * 100).toFixed(1);
  const growthUp  = (mrr.net_growth_30d || 0) >= 0;
  const projDelta = (mrr.projected_month_end_usd || 0) - (mrr.mrr_usd || 0);

  return (
    <div
      className="panel p-6 mb-8 relative overflow-hidden"
      data-testid="mrr-block"
      style={{
        background: "radial-gradient(ellipse at top left, rgba(90,60,10,0.25), rgba(10,5,3,0.95))",
        borderColor: "rgba(212,175,55,0.5)",
      }}
    >
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.45em] gold-text">MRR SNAPSHOT · SEASON PASS</div>
          <div className="font-heading text-2xl text-[#f0e6d2] leading-none mt-1">Recurring Revenue</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.3em] ash-text">AS OF · UTC</div>
          <div className="text-xs tracking-widest text-[#f0e6d2] mt-1">
            {mrr.as_of ? new Date(mrr.as_of).toUTCString().slice(0, 22) : "—"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <VaultCell testid="mrr-active" label="ACTIVE SUBS" primary={mrr.active_subscribers} sub="paying now" icon={<Users size={12} />} />
        <VaultCell
          testid="mrr-usd"
          label="MRR"
          primary={`$${(mrr.mrr_usd || 0).toFixed(2)}`}
          sub={`× $4.99/mo`}
          primaryColor="#d4af37"
          icon={<DollarSign size={12} />}
        />
        <VaultCell
          testid="mrr-growth"
          label="NET GROWTH · 30d"
          primary={`${growthUp ? "+" : ""}${mrr.net_growth_30d ?? 0}`}
          sub={<span style={{ color: growthUp ? "#7cffa0" : "#ff6b3a" }}>{growthPct}% rate</span>}
          primaryColor={growthUp ? "#7cffa0" : "#ff6b3a"}
          icon={growthUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        />
        <VaultCell
          testid="mrr-projected"
          label="MONTH-END EST."
          primary={`$${(mrr.projected_month_end_usd || 0).toFixed(2)}`}
          sub={
            <span style={{ color: projDelta >= 0 ? "#7cffa0" : "#ff6b3a" }}>
              {projDelta >= 0 ? "+" : ""}${Math.abs(projDelta).toFixed(2)}
            </span>
          }
          icon={<Repeat size={12} />}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MicroStat testid="mrr-new"      label="NEW SUBS · 30d"    value={mrr.new_subs_30d ?? 0}  color="#7cffa0" />
        <MicroStat testid="mrr-renewals" label="RENEWALS · 30d"    value={mrr.renewals_30d ?? 0}  color="#d4af37" />
        <MicroStat testid="mrr-churn"    label="CHURN · 30d"       value={mrr.churn_30d ?? 0}     color="#ff6b3a" />
        <MicroStat testid="mrr-onetime"  label="ONE-TIME · 30d"    value={`$${(mrr.one_time_revenue_30d_usd || 0).toFixed(2)}`} color="#f0e6d2" />
      </div>

      <div className="mt-4 text-[10px] tracking-widest ash-text">
        Total revenue in the last 30 days:{" "}
        <span className="gold-text">${(mrr.total_revenue_30d_usd || 0).toFixed(2)}</span>
      </div>
    </div>
  );
}

// ============================================================
// Cohorts
// ============================================================
function CohortsBlock({ cohorts }) {
  const rows = cohorts?.cohorts || [];
  const maxRev = Math.max(...rows.map((r) => r.revenue_30d_usd || 0), 0.01);

  return (
    <div className="mb-8" data-testid="cohorts-block">
      <div className="text-[10px] tracking-[0.4em] gold-text mb-2 flex items-center gap-1">
        <Coins size={12} /> COHORTS · BY ACQUISITION SOURCE (30d)
      </div>
      {!cohorts ? (
        <div className="panel p-6 text-center ash-text italic">Gathering the crows...</div>
      ) : rows.length === 0 ? (
        <div className="panel p-6 text-center ash-text italic">No cohorts yet — no attributed users.</div>
      ) : (
        <div className="panel overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.3em] ash-text">
                <th className="text-left px-4 py-3 border-b border-white/10">Source</th>
                <th className="text-right px-4 py-3 border-b border-white/10">Users</th>
                <th className="text-right px-4 py-3 border-b border-white/10">Revenue (30d)</th>
                <th className="text-right px-4 py-3 border-b border-white/10">ARPU</th>
                <th className="text-right px-4 py-3 border-b border-white/10">Payers</th>
                <th className="text-right px-4 py-3 border-b border-white/10">Conv %</th>
                <th className="px-4 py-3 border-b border-white/10" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.source} className="border-b border-white/5 last:border-0" data-testid={`cohort-row-${r.source}`}>
                  <td className="px-4 py-3 font-narrative text-[#f0e6d2]">{r.source}</td>
                  <td className="px-4 py-3 text-right text-[#f0e6d2]">{r.user_count}</td>
                  <td className="px-4 py-3 text-right gold-text">${(r.revenue_30d_usd || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-[#f0e6d2]">${(r.arpu_30d_usd || 0).toFixed(3)}</td>
                  <td className="px-4 py-3 text-right text-[#f0e6d2]">{r.payers}</td>
                  <td className="px-4 py-3 text-right text-[#f0e6d2]">{(r.payer_conversion_pct || 0).toFixed(1)}%</td>
                  <td className="px-4 py-2 w-1/4">
                    <div className="h-2 rounded-sm overflow-hidden bg-white/5">
                      <div
                        className="h-full transition-all"
                        style={{
                          width: `${Math.max(3, ((r.revenue_30d_usd || 0) / maxRev) * 100)}%`,
                          background: "linear-gradient(90deg, #d4af37, rgba(226,88,34,0.75))",
                          boxShadow: (r.revenue_30d_usd || 0) > 0 ? "0 0 6px rgba(212,175,55,0.4)" : "none",
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Ad fillrate
// ============================================================
function FillrateBlock({ fillrate }) {
  const rows = fillrate?.placements || [];
  return (
    <div className="mb-8" data-testid="fillrate-block">
      <div className="text-[10px] tracking-[0.4em] gold-text mb-2 flex items-center gap-1">
        <PlayCircle size={12} /> REWARDED ADS · LAST 7 DAYS
      </div>
      {!fillrate ? (
        <div className="panel p-6 text-center ash-text italic">Reading the smoke...</div>
      ) : rows.length === 0 ? (
        <div className="panel p-6 text-center ash-text italic">No ad events yet.</div>
      ) : (
        <div className="panel overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.3em] ash-text">
                <th className="text-left px-4 py-3 border-b border-white/10">Placement</th>
                <th className="text-right px-4 py-3 border-b border-white/10">Loaded</th>
                <th className="text-right px-4 py-3 border-b border-white/10">Rewarded</th>
                <th className="text-right px-4 py-3 border-b border-white/10">Dismissed</th>
                <th className="text-right px-4 py-3 border-b border-white/10">Failed</th>
                <th className="text-right px-4 py-3 border-b border-white/10">Unavailable</th>
                <th className="text-right px-4 py-3 border-b border-white/10">Fill %</th>
                <th className="text-right px-4 py-3 border-b border-white/10">Completion %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.placement} className="border-b border-white/5 last:border-0" data-testid={`fillrate-row-${r.placement}`}>
                  <td className="px-4 py-3 font-narrative text-[#f0e6d2]">{r.placement}</td>
                  <td className="px-4 py-3 text-right text-[#f0e6d2]">{r.loaded}</td>
                  <td className="px-4 py-3 text-right gold-text">
                    <span className="inline-flex items-center gap-1 justify-end">
                      <CheckCircle2 size={11} /> {r.rewarded}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-[#f0e6d2]">{r.dismissed}</td>
                  <td className="px-4 py-3 text-right blood-text">
                    <span className="inline-flex items-center gap-1 justify-end">
                      <XCircle size={11} /> {r.failed}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right ash-text">{r.unavailable}</td>
                  <td className="px-4 py-3 text-right text-[#f0e6d2]">{(r.fill_pct || 0).toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right gold-text">{(r.completion_pct || 0).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Small display helpers
// ============================================================
function VaultCell({ label, primary, sub, testid, primaryColor, icon }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] ash-text flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="font-heading text-3xl mt-1" style={{ color: primaryColor || "#f0e6d2" }}>
        {primary}
      </div>
      <div className="text-[10px] tracking-widest ash-text mt-0.5">{sub}</div>
    </div>
  );
}

function MicroStat({ label, value, color, testid }) {
  return (
    <div className="panel p-3" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] ash-text">{label}</div>
      <div className="font-heading text-xl mt-1" style={{ color: color || "#f0e6d2" }}>{value}</div>
    </div>
  );
}


// ============================================================
// iter67.3 — Dynamic Ad-Free Pass Pricing
// ============================================================
function DynamicPriceBlock({ dynPrice }) {
  if (!dynPrice) {
    return <div className="panel p-6 mb-8 ash-text italic text-center">Reading the pricing ember...</div>;
  }
  const discounted = !!dynPrice.discounted;
  return (
    <div
      className="panel p-6 mb-8"
      data-testid="dynamic-price-block"
      style={{
        background: discounted
          ? "linear-gradient(135deg, rgba(192,57,43,0.15), rgba(10,5,3,0.95))"
          : "radial-gradient(ellipse at top, rgba(40,25,10,0.4), rgba(10,5,3,0.98))",
        borderColor: discounted ? "rgba(192,57,43,0.6)" : "rgba(212,175,55,0.4)",
      }}
    >
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.4em] ash-text">AD-FREE PASS · DYNAMIC PRICE</div>
          <div className="font-heading text-3xl mt-1" style={{ color: discounted ? "#ffb56b" : "#d4af37" }}>
            {dynPrice.price_display}
          </div>
          {discounted && (
            <div className="text-[11px] mt-1 italic" style={{ color: "#ff6b3a" }} data-testid="dyn-price-discount-note">
              Discounted from ${(dynPrice.base_cents / 100).toFixed(2)} — auto-cut when fill-rate &lt; 60%.
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3 text-right">
          <MicroStat
            label="FILL (24H)"
            value={`${(dynPrice.fill_pct || 0).toFixed(1)}%`}
            color={dynPrice.fill_pct < 60 ? "#ff6b3a" : "#7cffa0"}
            testid="dyn-price-fill"
          />
          <MicroStat
            label="SAMPLE"
            value={dynPrice.sample_size}
            testid="dyn-price-sample"
          />
          <MicroStat
            label="STATUS"
            value={discounted ? "DISCOUNTED" : "FULL"}
            color={discounted ? "#ff6b3a" : "#7cffa0"}
            testid="dyn-price-status"
          />
        </div>
      </div>
      {dynPrice.sample_size < 50 && (
        <div className="text-[10px] tracking-widest ash-text mt-3 italic">
          Needs ≥ 50 ad events in last 24h before the discount can trigger (currently {dynPrice.sample_size}).
        </div>
      )}
    </div>
  );
}

// ============================================================
// iter67.3 — Fill-rate alert banner (2-day consecutive-low check)
// ============================================================
function FillAlertBanner({ fillAlerts }) {
  if (!fillAlerts) return null;
  const triggered = fillAlerts.triggered_count > 0;
  const latest = fillAlerts.latest;
  if (!latest) {
    return (
      <div className="panel p-4 mb-8 ash-text italic text-sm" data-testid="fill-alert-banner-empty">
        No fill-rate alert runs yet — the cron fires daily at 07:00 UTC.
      </div>
    );
  }
  return (
    <div
      className="panel p-5 mb-8"
      data-testid="fill-alert-banner"
      style={{
        background: triggered
          ? "linear-gradient(135deg, rgba(192,57,43,0.25), rgba(10,5,3,0.98))"
          : "radial-gradient(ellipse at top, rgba(40,25,10,0.3), rgba(10,5,3,0.98))",
        borderColor: triggered ? "rgba(192,57,43,0.75)" : "rgba(124,255,160,0.3)",
        boxShadow:   triggered ? "0 0 30px rgba(192,57,43,0.4)" : "none",
      }}
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.4em]" style={{ color: triggered ? "#ff6b3a" : "#7cffa0" }}>
            {triggered ? "⚠  FILL-RATE ALERT · 2-DAY STREAK" : "✓  FILL-RATE HEALTHY"}
          </div>
          <div className="font-heading text-xl mt-2 text-[#f0e6d2]">
            Today: {latest.today?.fill_pct?.toFixed(1)}% · Yesterday: {latest.yesterday?.fill_pct?.toFixed(1)}%
          </div>
          <div className="text-[11px] ash-text italic mt-1">
            Threshold {latest.threshold_pct}% · {fillAlerts.triggered_count} alert{fillAlerts.triggered_count === 1 ? "" : "s"} in last 30 days
          </div>
        </div>
        <div className="text-right text-[10px] tracking-widest ash-text">
          Last cron: {new Date(latest.ran_at).toLocaleString()}
          <br />
          Email: {latest.email_sent ? "SENT" : (latest.email_reason || (triggered ? "pending" : "n/a"))}
        </div>
      </div>
    </div>
  );
}

